import { useState } from 'react';
import type { Me, Project } from '@hangar/shared';
import { useMe } from '@/api/hooks';
import { config } from '@/config';
import { roleLabel } from '@/lib/format';
import { useRunBuildGate } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { AppMark } from '@/components/Logo';
import { ThemeSegmented } from '@/components/ThemeSwitch';
import { UserAvatar } from '@/components/UserAvatar';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { moreGroups, type NavItem } from './nav';
import { canChangePassword } from '@/auth/changePassword';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import { joinLabel, pendingInvitationsOf, switcherOrgs } from './shellModel';
import { useOrgActions } from './useOrgActions';
import { TAB_SLUGS, identityOf, orgLabel, tabItems, topBarTitle } from './shellModel';
import { NavLink } from './NavLink';
import { useRunBuild } from './RunBuildContext';

function TopBarTitle({ me, project }: { me: Me | undefined; project: Project | undefined }) {
  return (
    <div className="min-w-0 flex-1">
      <div className="truncate text-[15px] font-semibold leading-tight text-foreground">{topBarTitle(project, me)}</div>
      <div className="truncate font-mono text-[11px] text-subtle">
        {orgLabel(me)} · v{config.version}
      </div>
    </div>
  );
}

/** The trigger's content (the Button itself stays under SheetTrigger so Radix can hand it its props). */
function AccountAvatar({ me }: { me: Me | undefined }) {
  const { name, email } = identityOf(me);
  return (
    <>
      {me ? <UserAvatar name={name} email={email} size={36} /> : <Skeleton className="size-9 rounded-full" />}
      {me ? (
        <span data-testid="me-email" className="sr-only">
          {email}
        </span>
      ) : null}
    </>
  );
}

function AccountIdentity({ me }: { me: Me }) {
  const { name, email } = identityOf(me);
  return (
    <div className="flex items-center gap-3 px-2 py-2">
      <UserAvatar name={name} email={email} size={44} />
      <div className="min-w-0">
        <div className="truncate text-[15px] font-medium text-foreground">{name}</div>
        <div className="truncate text-xs text-subtle">
          {roleLabel(me)} · <span className="font-mono">{email}</span>
        </div>
      </div>
    </div>
  );
}

/** The phone's org switcher (the sidebar's menu on larger screens): one 44 px row per org, the current one checked, then the invitations to join. */
function PhoneOrgList({ me }: { me: Me | undefined }) {
  const { switchOrg, joinOrg } = useOrgActions();
  const orgs = switcherOrgs(me);
  const invitations = pendingInvitationsOf(me);
  if (!me || !orgs.length) return null;
  const row = 'flex min-h-11 w-full items-center gap-2 rounded-lg px-3 text-left text-sm';
  return (
    <div className="px-2 py-2">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-subtle">Organization</div>
      <ul className="flex flex-col gap-1">
        {orgs.map((o) => (
          <li key={o.id}>
            <button type="button" data-testid={`org-option-${o.slug}`} aria-current={o.id === me.org.id ? 'true' : undefined} onClick={() => o.id !== me.org.id && switchOrg(o.id)}
              className={cn(row, o.id === me.org.id ? 'bg-accent font-semibold text-foreground' : 'text-muted-foreground hover:bg-accent')}>
              <span className="min-w-0 flex-1 truncate">{o.name}</span>
              {o.id === me.org.id ? <Icon name="check" size={18} className="text-primary" /> : null}
            </button>
          </li>
        ))}
        {invitations.map((p) => (
          <li key={p.id}>
            <button type="button" data-testid={`org-join-${p.org.slug}`} onClick={() => void joinOrg(p.id)} className={cn(row, 'text-foreground hover:bg-accent')}>
              <Icon name="person_add" size={18} className="text-primary" />
              <span className="min-w-0 flex-1 truncate">{joinLabel(p)}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AccountSheet({ me, onSignOut, onChangePassword }: { me: Me | undefined; onSignOut: () => void; onChangePassword?: () => void }) {
  return (
    <SheetContent side="bottom" aria-describedby={undefined}>
      <SheetHeader>
        <SheetTitle>Account</SheetTitle>
      </SheetHeader>
      <div className="p-2">
        {me ? <AccountIdentity me={me} /> : null}
        <PhoneOrgList me={me} />
        <div className="px-2 py-2">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-subtle">Theme</div>
          <ThemeSegmented />
        </div>
        <div className="flex flex-col gap-2 px-2 pb-2 pt-1">
          {onChangePassword ? (
            <Button variant="outline" data-testid="change-password-open" className="w-full" onClick={onChangePassword}>
              Change password
            </Button>
          ) : null}
          <Button variant="outline" data-testid="sign-out" className="w-full" onClick={onSignOut}>
            Sign out
          </Button>
        </div>
      </div>
    </SheetContent>
  );
}

export function MobileTopBar({ me, project, onSignOut }: { me: Me | undefined; project: Project | undefined; onSignOut: () => void }) {
  const [open, setOpen] = useState(false);
  const [changing, setChanging] = useState(false);
  // The sheet closes first so the dialog is the only overlay; tokens, a preview and the dev issuer have no password here.
  const onChangePassword = canChangePassword() && me?.user && !me.preview ? () => { setOpen(false); setChanging(true); } : undefined;
  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b bg-card px-4">
      <AppMark size={36} />
      <TopBarTitle me={me} project={project} />
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" aria-label="Account" className="rounded-full">
            <AccountAvatar me={me} />
          </Button>
        </SheetTrigger>
        <AccountSheet me={me} onSignOut={onSignOut} onChangePassword={onChangePassword} />
      </Sheet>
      <ChangePasswordDialog open={changing} onOpenChange={setChanging} />
    </header>
  );
}

const TAB_CLASS = cn(
  'flex h-full flex-col items-center justify-center gap-1 text-[11px] font-medium text-muted-foreground transition-colors duration-150',
  'aria-[current=page]:text-primary',
);

function TabLink({ item, currentSlug }: { item: NavItem; currentSlug: string | undefined }) {
  return (
    <li>
      <NavLink slug={item.slug} to={item.href(currentSlug)} data-testid={`nav-${item.slug}`} className={TAB_CLASS}>
        <Icon name={item.icon} size={22} />
        {item.short ?? item.label}
      </NavLink>
    </li>
  );
}

const MORE_ROW = 'flex h-12 items-center gap-3 rounded-lg px-3 text-[15px]';

function MoreLiveRow({ item, currentSlug, onNavigate }: { item: NavItem; currentSlug: string | undefined; onNavigate: () => void }) {
  return (
    <li>
      <NavLink slug={item.slug} to={item.href(currentSlug)} data-testid={`nav-${item.slug}`} onClick={onNavigate} className={cn(MORE_ROW, 'text-foreground hover:bg-accent aria-[current=page]:text-primary')}>
        <Icon name={item.icon} size={22} />
        <span className="flex-1 truncate">{item.label}</span>
        <Icon name="chevron_right" size={20} className="text-subtle" />
      </NavLink>
    </li>
  );
}

function MoreSheet({ currentSlug, onNavigate }: { currentSlug: string | undefined; onNavigate: () => void }) {
  const me = useMe().data;
  return (
    <SheetContent side="bottom" aria-describedby={undefined}>
      <SheetHeader>
        <SheetTitle>More</SheetTitle>
      </SheetHeader>
      <div className="flex max-h-[70dvh] flex-col overflow-y-auto p-2">
        {moreGroups(me, TAB_SLUGS).map((group) => (
          <div key={group.id} role="group" aria-label={group.label ?? undefined} data-testid={`nav-group-${group.id}`}>
            {group.label ? <div className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-subtle" data-testid={`nav-group-label-${group.id}`}>{group.label}</div> : null}
            <ul className="flex flex-col">
              {group.items.map((item) => <MoreLiveRow key={item.slug} item={item} currentSlug={currentSlug} onNavigate={onNavigate} />)}
            </ul>
          </div>
        ))}
      </div>
    </SheetContent>
  );
}

/** Phone tab bar (mobile mockups): Overview · Builds · Apps · More; the More sheet lists the other sections. */
export function MobileTabBar({ currentSlug }: { currentSlug: string | undefined }) {
  const [more, setMore] = useState(false);
  return (
    <nav aria-label="Primary" className="fixed inset-x-0 bottom-0 z-30 border-t bg-card pb-[env(safe-area-inset-bottom)]">
      <ul className="grid h-16 grid-cols-4">
        {tabItems().map((item) => (
          <TabLink key={item.slug} item={item} currentSlug={currentSlug} />
        ))}
        <li>
          <Sheet open={more} onOpenChange={setMore}>
            <SheetTrigger asChild>
              <button type="button" data-testid="nav-more" className={cn(TAB_CLASS, 'w-full')}>
                <Icon name="more_horiz" size={22} />
                More
              </button>
            </SheetTrigger>
            <MoreSheet currentSlug={currentSlug} onNavigate={() => setMore(false)} />
          </Sheet>
        </li>
      </ul>
    </nav>
  );
}

/**
 * Floating "Run build" from the phone mockup; opens the Run build sheet. Not rendered for guests and clients; disabled
 * with its reason without `builds:write` or when the API reports no console Run build (`features.runBuild`).
 */
export function RunBuildFab({ projectSlug, projectId }: { projectSlug?: string; projectId?: string }) {
  const { openRunBuild } = useRunBuild();
  const gate = useRunBuildGate(projectId);
  if (gate.can.hidden) return null;
  return (
    <div className="fixed bottom-[calc(76px+env(safe-area-inset-bottom))] right-4 z-30">
      <GatedButton size="lg" data-testid="run-build-open" className="rounded-full px-5 shadow-lift" can={gate.can} blockedReason={gate.blockedReason} onClick={() => openRunBuild({ projectSlug })}>
        <Icon name="add" size={18} />
        Run build
      </GatedButton>
    </div>
  );
}
